import { ChevronRightIcon } from '@forwardflow/icons'
import {
   EAC_NEW_LEAD,
   EAC_COLD_LEAD,
   BASE_SEAT_COST,
   ADDITIONAL_SEAT_COST,
} from '@forwardflow/constants/onboarding.constants'
import { OnboardingV2Service } from '@forwardflow/services/onboardingV2.service'
import { useAppStore } from '@forwardflow/store/appStore'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import { Button, ContentCard, Stack } from '@forwardflow/ui-v2'
import { Grid, NumberInput, Slider } from '@mantine/core'
import { useState, useMemo, useEffect } from 'react'
import { useSubscriptionPackageLists } from '@forwardflow/hooks/customHooks'
import { isPaymentPricingReady } from '../payment-pricing'

const MIN_BUDGET = 100

export const PricingStepV3 = () => {
   const { onboardingData, getNextStep, setOrganizationId, setPricingData } =
      useOnboardingStoreV2()
   const { setCurrentOrganization } = useAppStore()
   const { email, isSubscriptionActivated, pricingData } = onboardingData
   const [isOnboardingStarting, setIsOnboardingStarting] = useState(false)

   const { data: subscriptionPackages } = useSubscriptionPackageLists({
      page: 1,
      limit: 10,
   })

   useEffect(() => {
      if (subscriptionPackages) {
         const selectedTier = subscriptionPackages?.pricingTiers?.[0]
         if (selectedTier && !pricingData?.selectedTierId) {
            setPricingData({ ...pricingData, selectedTierId: selectedTier.id })
         }
      }
   }, [subscriptionPackages])

   const seatsCount = pricingData?.seatsCount ?? 1

   const [maxSpend, setMaxSpend] = useState(() => {
      return pricingData?.maxSpend ?? 150
   })

   // Split percentage: 0 = all revive, 100 = all new leads
   const [splitPercent, setSplitPercent] = useState(() => {
      const storedMaxSpend = pricingData?.maxSpend ?? 150
      const storedNewBudget = pricingData?.newLeadBudget ?? 93
      if (storedMaxSpend === 0) return 50
      return Math.round((storedNewBudget * 100) / storedMaxSpend)
   })

   const seatCost = useMemo(() => {
      return BASE_SEAT_COST + (seatsCount - 1) * ADDITIONAL_SEAT_COST
   }, [seatsCount])

   const newLeadBudget = useMemo(() => {
      return Math.round((maxSpend * splitPercent) / 100)
   }, [maxSpend, splitPercent])

   const reactivateLeadBudget = useMemo(() => {
      return maxSpend - newLeadBudget
   }, [maxSpend, newLeadBudget])

   // Derived lead counts for display only
   const calculatedNewLeads = useMemo(() => {
      return Math.floor(newLeadBudget / EAC_NEW_LEAD)
   }, [newLeadBudget])

   const calculatedReviveLeads = useMemo(() => {
      return Math.floor(reactivateLeadBudget / EAC_COLD_LEAD)
   }, [reactivateLeadBudget])

   // Sync budgets to store
   useEffect(() => {
      setPricingData({
         ...pricingData,
         newLeadBudget,
         reactivateLeadBudget,
         maxSpend,
      })
   }, [newLeadBudget, reactivateLeadBudget, maxSpend])

   const handleSplitChange = (value: number) => {
      setSplitPercent(value)
   }

   const handleMaxSpendChange = (value: number) => {
      setMaxSpend(Math.max(0, value || 0))
   }

   const handleSeatsChange = (value: number) => {
      setPricingData({
         ...pricingData,
         seatsCount: value,
      })
   }

   const handleStartOnboarding = async () => {
      try {
         if (!email) return
         // Persist the displayed defaults on confirmation, not just values the
         // user edited. Otherwise an untouched seat count never reaches payment.
         const confirmedPricing = {
            ...pricingData,
            seatsCount,
            newLeadBudget,
            reactivateLeadBudget,
            maxSpend,
         }
         if (!isPaymentPricingReady(confirmedPricing)) return
         setPricingData(confirmedPricing)
         setIsOnboardingStarting(true)
         const data = await OnboardingV2Service.startOnboarding({ email })
         const organizationId = data?.data?.id
         if (data?.success && organizationId) {
            setCurrentOrganization(organizationId)
            setOrganizationId(organizationId)
            getNextStep()
         }
      } catch (error) {
         console.error(error)
      } finally {
         setIsOnboardingStarting(false)
      }
   }

   return (
      <Stack gap={32}>
         <ContentCard className="space-y-2">
            <p className="text-white font-medium">
               Welcome to Forward Flow! Pick the perfect plan for your team
            </p>
            <p className="text-white/70 text-sm font-medium">
               Choose a billing cycle and a plan that fits your team. You can
               switch anytime from your account settings.
            </p>
         </ContentCard>

         {/* Team Size */}
         <ContentCard className="!space-y-4">
            <Stack gap={4}>
               <p className="text-white font-medium">How big is your team?</p>
               <p className="text-[#99A1AF] text-sm">
                  $97 for first user + $50 for each additional user
               </p>
            </Stack>
            <Grid className="items-center">
               <Grid.Col span={8}>
                  <div className="bg-white/[0.02] rounded-lg p-4">
                     <Grid className="items-center">
                        <Grid.Col span={4}>
                           <NumberInput
                              min={1}
                              max={50}
                              value={seatsCount}
                              classNames={{
                                 input: 'border border-[#B17AFF]/[0.5] rounded-lg p-2',
                                 section: 'w-20',
                              }}
                              onChange={(value) =>
                                 handleSeatsChange(Number(value))
                              }
                              rightSection={
                                 <span className="text-white/60 text-xs">
                                    Users
                                 </span>
                              }
                           />
                        </Grid.Col>
                        <Grid.Col span={8} className="flex flex-col justify-center">
                           <Slider
                              min={1}
                              max={50}
                              value={seatsCount}
                              classNames={{
                                 track: '!bg-[#D9D9D9]/10 !h-2 rounded-full',
                                 bar: '!bg-gradient-to-r !from-blue-500 !to-purple-500 !h-2',
                                 thumb: '!bg-white !w-4 !h-4 !rounded-full !border-0',
                              }}
                              onChange={(value) => handleSeatsChange(value)}
                           />
                        </Grid.Col>
                     </Grid>
                  </div>
               </Grid.Col>
               <Grid.Col span={4}>
                  <div
                     className="rounded-xl p-4 px-8 border border-[#EB69C2]/[0.5] text-right"
                     style={{
                        background:
                           'linear-gradient(111deg, rgba(159, 123, 255, 0.10) 1.32%, rgba(235, 105, 194, 0.10) 96.08%)',
                     }}
                  >
                     <p className="text-white/50 text-sm font-medium">
                        Seat Cost
                     </p>
                     <p className="text-white font-semibold text-lg">
                        ${seatCost}/month
                     </p>
                  </div>
               </Grid.Col>
            </Grid>
         </ContentCard>

         {/* Lead Budget */}
         <ContentCard className="!space-y-5">
            <Stack gap={4}>
               <p className="text-white font-medium">
                  Set your monthly lead budget
               </p>
               <p className="text-[#99A1AF] text-sm">
                  Set a maximum monthly spend, then adjust how it's split between
                  new leads (${EAC_NEW_LEAD}/lead) and revive leads ($
                  {EAC_COLD_LEAD}/lead).
               </p>
            </Stack>

            {/* Max Spend */}
            <div className="bg-white/[0.02] rounded-lg p-4 flex items-center justify-between">
               <p className="text-white font-medium">Maximum Monthly Spend</p>
               <div className="flex items-center gap-2">
                  <NumberInput
                     min={0}
                     max={50000}
                     value={maxSpend}
                     prefix="$"
                     hideControls
                     classNames={{
                        input: 'border border-[#B17AFF]/[0.5] rounded-lg p-2 text-right',
                        root: 'w-[150px]',
                     }}
                     onChange={(value) =>
                        handleMaxSpendChange(Number(value))
                     }
                  />
                  <span className="text-white/60 text-sm">/mo</span>
               </div>
            </div>

            {/* Split Slider */}
            <div className="bg-white/[0.02] rounded-lg p-4">
               <div className="flex items-center justify-between mb-3">
                  <p className="text-white/70 text-sm">Budget Split</p>
                  <p className="text-white/50 text-xs">
                     {splitPercent}% New / {100 - splitPercent}% Revive
                  </p>
               </div>
               <Slider
                  min={0}
                  max={100}
                  value={splitPercent}
                  classNames={{
                     track: '!bg-[#D9D9D9]/10 !h-2 rounded-full',
                     bar: '!bg-gradient-to-r !from-[#9F7BFF] !to-[#EB69C2] !h-2',
                     thumb: '!bg-white !w-4 !h-4 !rounded-full !border-0',
                  }}
                  onChange={handleSplitChange}
                  label={(value) => `${value}% New Leads`}
               />
               <div className="flex justify-between mt-2">
                  <span className="text-white/40 text-xs">All Revive</span>
                  <span className="text-white/40 text-xs">All New Leads</span>
               </div>
            </div>

            {/* Lead Counts */}
            <Grid>
               <Grid.Col span={6}>
                  <div
                     className="rounded-xl p-4 border border-[#9F7BFF]/30"
                     style={{
                        background: 'rgba(159, 123, 255, 0.05)',
                     }}
                  >
                     <p className="text-white/50 text-sm mb-2">New Leads <span className="text-white/30">(${EAC_NEW_LEAD}/lead)</span></p>
                     <div className="flex items-center justify-between">
                        <p className="text-white font-semibold text-2xl">
                           {calculatedNewLeads.toLocaleString()}
                        </p>
                        <div className="text-right">
                           <p className="text-white font-semibold">
                              ${newLeadBudget}
                           </p>
                           <p className="text-white/40 text-xs">
                              /month
                           </p>
                        </div>
                     </div>
                  </div>
               </Grid.Col>
               <Grid.Col span={6}>
                  <div
                     className="rounded-xl p-4 border border-[#EB69C2]/30"
                     style={{
                        background: 'rgba(235, 105, 194, 0.05)',
                     }}
                  >
                     <p className="text-white/50 text-sm mb-2">Revive Leads <span className="text-white/30">(${EAC_COLD_LEAD}/lead)</span></p>
                     <div className="flex items-center justify-between">
                        <p className="text-white font-semibold text-2xl">
                           {calculatedReviveLeads.toLocaleString()}
                        </p>
                        <div className="text-right">
                           <p className="text-white font-semibold">
                              ${reactivateLeadBudget}
                           </p>
                           <p className="text-white/40 text-xs">
                              /month
                           </p>
                        </div>
                     </div>
                  </div>
               </Grid.Col>
            </Grid>
         </ContentCard>

         {maxSpend < MIN_BUDGET && (
            <p className="text-red-400 text-sm">
               Minimum monthly budget is ${MIN_BUDGET}
            </p>
         )}
         <Button
            className="w-full"
            onClick={handleStartOnboarding}
            loading={isOnboardingStarting}
            disabled={maxSpend < MIN_BUDGET || !isPaymentPricingReady({
               ...pricingData, seatsCount, newLeadBudget, reactivateLeadBudget, maxSpend,
            })}
         >
            {isSubscriptionActivated
               ? 'Continue'
               : 'Confirm Plan and Continue'}{' '}
            <ChevronRightIcon width={18} />
         </Button>
      </Stack>
   )
}
